import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PaperPlaneTilt, Plus, Play, Pause, Stop, Trash, Eye, Lightning,
  ChartBar, EnvelopeSimple, EnvelopeOpen, CursorClick, ChatCircle,
  Users, Globe, Brain, CircleNotch, Check, Warning, ArrowRight,
  Funnel, MagnifyingGlass, Translate, ArrowsClockwise, X,
  PaperPlaneRight, CheckCircle, XCircle, Clock, Envelope,
  CaretDown, CaretUp, PencilSimple, FloppyDisk, Eye as EyeIcon,
  TestTube, Trophy, Percent, ArrowUp, ArrowDown, Star, Copy, ListBullets, UserPlus
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TranslateButton, TranslateBackButton } from '../components/TranslateButton';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  API, STATUS_COLORS, REVIEW_STATUS_STYLES, CATEGORY_STYLES,
  URGENCY_COLORS, LANGUAGES, SEQUENCE_STEPS,
} from '../components/marketing/constants';
import { SelfLearningPanel } from '../components/marketing/SelfLearningPanel';
import { SendWindowSettings } from '../components/marketing/SendWindowSettings';
import { InboxCleanupSettings } from '../components/marketing/InboxCleanupSettings';
import { InboxAutoSyncSettings } from '../components/marketing/InboxAutoSyncSettings';
import { QueueSettings } from '../components/marketing/QueueSettings';
import DailyDigestSettings from '../components/DailyDigestSettings';
import SocialProofSettings from '../components/SocialProofSettings';
import SequenceOverviewTab from '../components/SequenceOverviewTab';
import { GraphStatusBanner } from '../components/marketing/GraphStatusBanner';
import { BroadcastEditor } from '../components/marketing/BroadcastEditor';
import { BroadcastsList, BroadcastDetailPanel } from '../components/marketing/BroadcastsList';
import CampaignProductModal from '../components/CampaignProductModal';
import CampaignTemplatesList from '../components/CampaignTemplatesList';
import SequenceLibraryModal from '../components/SequenceLibraryModal';
import { CampaignEditModal } from '../components/marketing/CampaignEditModal';
import { CampaignDuplicateModal } from '../components/marketing/CampaignDuplicateModal';
import CampaignQueueInspectorModal from '../components/marketing/CampaignQueueInspectorModal';
import AddNewContactsModal from '../components/marketing/AddNewContactsModal';

/* ─── MAIN PAGE ─────────────────────────────────────────────────── */
export default function EmailCampaignsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sequence Health Score dashboard data
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [showOnlyAttention, setShowOnlyAttention] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [newCampaign, setNewCampaign] = useState({ name: '', type: 'sequence', segment_filter: {}, generate_on_send: false, sender_email: '', signature_html: '' });
  // Mailboxes for sender selection
  const [mailboxes, setMailboxes] = useState([]);
  // Test-mail state
  const [testMail, setTestMail] = useState({ open: false, to: '', sending: false, result: null });
  // Template generation
  const [generating, setGenerating] = useState(false);
  const [genLanguage, setGenLanguage] = useState('nl');
  const [genStep, setGenStep] = useState('introductie');
  const [genSegment, setGenSegment] = useState('zwembadbeheerder');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [genFacilityName, setGenFacilityName] = useState('');
  const [genCity, setGenCity] = useState('');
  const [genContactName, setGenContactName] = useState('');
  const [genExtraInfo, setGenExtraInfo] = useState('');
  const [generatingSeq, setGeneratingSeq] = useState(false);
  const [generatedSequence, setGeneratedSequence] = useState(null);
  // Segment preview
  const [segmentCount, setSegmentCount] = useState(null);
  const [segmentFilter, setSegmentFilter] = useState({});
  // Inbox
  const [inboxItems, setInboxItems] = useState([]);
  const [inboxTotal, setInboxTotal] = useState(0);
  const [inboxStats, setInboxStats] = useState(null);
  const [inboxFilter, setInboxFilter] = useState({ status: '', category: '' });
  const [selectedReply, setSelectedReply] = useState(null);
  const [detailReply, setDetailReply] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [activeTab, setActiveTab] = useState('campaigns');
  // Step Configuration
  const [stepConfigs, setStepConfigs] = useState([]);
  const [editingSteps, setEditingSteps] = useState(false);
  const [broadcastCampaign, setBroadcastCampaign] = useState(null);
  const [editCampaign, setEditCampaign] = useState(null);
  const [duplicateCampaign, setDuplicateCampaign] = useState(null);
  const [queueCampaign, setQueueCampaign] = useState(null);
  const [addContactsCampaign, setAddContactsCampaign] = useState(null);
  const [broadcastStatsId, setBroadcastStatsId] = useState(null);
  // Per-campaign Product & Sequence modal
  const [productModalCampaign, setProductModalCampaign] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [editStepFocus, setEditStepFocus] = useState({});
  const [stepSaving, setStepSaving] = useState(null);
  // Analytics dashboard
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  // Campaign activation
  const [activateDialog, setActivateDialog] = useState(null);
  const [activating, setActivating] = useState(false);
  // A/B Tests
  const [abTests, setAbTests] = useState([]);
  const [abStats, setAbStats] = useState(null);
  const [showCreateAB, setShowCreateAB] = useState(false);
  const [abCreating, setAbCreating] = useState(false);
  const [abForm, setAbForm] = useState({
    name: '', dimension: 'subject', winning_metric: 'open_rate',
    variant_a: { label: 'Variant A', subject: '', text_body: '', html_body: '' },
    variant_b: { label: 'Variant B', subject: '', text_body: '', html_body: '' },
  });
  const [aiGeneratingVariants, setAiGeneratingVariants] = useState(false);
  const [abActionLoading, setAbActionLoading] = useState('');
  // Auto-Optimization
  const [optInsights, setOptInsights] = useState(null);
  const [optHistory, setOptHistory] = useState([]);
  const [optRunning, setOptRunning] = useState(false);
  const [optBatchRunning, setOptBatchRunning] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  const fetchOverview = useCallback(() => {
    fetch(`${API}/email/analytics/overview`, { credentials: 'include' })
      .then(r => r.json()).then(setOverview).catch(() => {});
  }, []);

  const fetchCampaigns = useCallback(() => {
    fetch(`${API}/email/campaigns`, { credentials: 'include' })
      .then(r => r.json()).then(d => setCampaigns(d.campaigns || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchInbox = useCallback(() => {
    const params = new URLSearchParams();
    if (inboxFilter.status) params.set('status', inboxFilter.status);
    if (inboxFilter.category) params.set('category', inboxFilter.category);
    params.set('limit', '50');
    fetch(`${API}/email/inbox?${params}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setInboxItems(d.replies || []); setInboxTotal(d.total || 0); }).catch(() => {});
  }, [inboxFilter]);

  const fetchInboxStats = useCallback(() => {
    fetch(`${API}/email/inbox/stats`, { credentials: 'include' })
      .then(r => r.json()).then(setInboxStats).catch(() => {});
  }, []);

  const fetchReplyDetail = useCallback((replyId) => {
    fetch(`${API}/email/inbox/${replyId}`, { credentials: 'include' })
      .then(r => r.json()).then(setDetailReply).catch(() => {});
  }, []);

  useEffect(() => { fetchOverview(); fetchCampaigns(); fetchInboxStats(); }, [fetchOverview, fetchCampaigns, fetchInboxStats]);

  // Load Sequence Health Scores once on page mount (and refresh every 2 min)
  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const r = await fetch(`${API}/email/campaign-health-scores`, { credentials: 'include' });
      const d = await r.json();
      if (r.ok) setHealthData(d);
    } catch { /* silent */ }
    setHealthLoading(false);
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 120000);
    return () => clearInterval(id);
  }, [fetchHealth]);

  const fetchStepConfigs = useCallback(() => {
    fetch(`${API}/email/step-configs`, { credentials: 'include' })
      .then(r => r.json()).then(d => setStepConfigs(d.configs || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchStepConfigs(); }, [fetchStepConfigs]);

  useEffect(() => {
    if (activeTab === 'analytics' && !dashData && !dashLoading) {
      setDashLoading(true);
      fetch(`${API}/email/analytics/dashboard`, { credentials: 'include' })
        .then(r => r.json()).then(setDashData).catch(() => {}).finally(() => setDashLoading(false));
    }
  }, [activeTab, dashData, dashLoading]);

  const saveStepConfig = async (step) => {
    setStepSaving(step);
    await fetch(`${API}/email/step-configs/${step}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ focus: editStepFocus[step] }),
    });
    fetchStepConfigs();
    setStepSaving(null);
  };

  const [aiSequenceLoading, setAiSequenceLoading] = useState(false);
  const aiGenerateSequence = async () => {
    if (stepConfigs.length > 0 && !window.confirm('De huidige 6-stappen sequence wordt overschreven door een nieuwe AI-versie afgestemd op de profiel-beschrijving en zoektermen. Doorgaan?')) return;
    setAiSequenceLoading(true);
    try {
      const r = await fetch(`${API}/email/step-configs/ai-generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overwrite: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Sequence generatie mislukt');
      window.alert(`✓ Nieuwe 6-stappen sequence gegenereerd voor profiel "${d.profile}".`);
      fetchStepConfigs();
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setAiSequenceLoading(false);
    }
  };
  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const fetchAbTests = useCallback(() => {
    fetch(`${API}/email/ab-tests`, { credentials: 'include' })
      .then(r => r.json()).then(d => setAbTests(d.tests || [])).catch(() => {});
  }, []);

  const fetchAbStats = useCallback(() => {
    fetch(`${API}/email/ab-tests/stats`, { credentials: 'include' })
      .then(r => r.json()).then(setAbStats).catch(() => {});
  }, []);

  useEffect(() => { fetchAbTests(); fetchAbStats(); }, [fetchAbTests, fetchAbStats]);

  const openDetail = (reply) => {
    setSelectedReply(reply.reply_id);
    fetchReplyDetail(reply.reply_id);
  };

  const closeDetail = () => { setSelectedReply(null); setDetailReply(null); };

  const onDetailAction = () => {
    closeDetail();
    fetchInbox();
    fetchInboxStats();
    fetchOverview();
  };

  const createCampaign = async () => {
    setCreating(true);
    let createdId = null;
    try {
      if (newCampaign.type === 'sequence' && newCampaign.template_id) {
        // Spawn from saved template — copies product + sequence_steps + filter
        const r = await fetch(`${API}/campaign-templates/${newCampaign.template_id}/spawn`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newCampaign.name || undefined }),
        });
        const d = await r.json();
        createdId = d.campaign_id;
        // Patch sender + signature on the spawned campaign
        if (createdId && (newCampaign.sender_email || newCampaign.signature_html)) {
          await fetch(`${API}/email/campaigns/${createdId}`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sender_email: newCampaign.sender_email,
              signature_html: newCampaign.signature_html,
            }),
          });
        }
      } else {
        const r = await fetch(`${API}/email/campaigns`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newCampaign),
        });
        const d = await r.json();
        createdId = d.campaign_id;
      }
    } finally {
      setCreating(false);
    }
    setShowCreate(false);
    setNewCampaign({ name: '', type: 'sequence', segment_filter: {}, generate_on_send: false });
    await fetchCampaigns();
    // For sequence campaigns: directly open the Product modal so user can tweak focus per step
    if (createdId && (newCampaign.type === 'sequence')) {
      try {
        const r = await fetch(`${API}/email/campaigns/${createdId}`, { credentials: 'include' });
        const c = await r.json();
        setProductModalCampaign(c);
      } catch { /* ignore */ }
    }
  };

  const campaignAction = async (id, action) => {
    await fetch(`${API}/email/campaigns/${id}/${action}`, { method: 'POST', credentials: 'include' });
    fetchCampaigns(); fetchOverview();
  };

  const confirmActivate = async (campaignId) => {
    // Get segment count first
    setActivateDialog({ id: campaignId, loading: true, count: null });
    try {
      const res = await fetch(`${API}/email/segments/count`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setActivateDialog({ id: campaignId, loading: false, count: data.count || 0 });
    } catch {
      setActivateDialog({ id: campaignId, loading: false, count: '?' });
    }
  };

  const doActivate = async () => {
    if (!activateDialog) return;
    setActivating(true);
    const res = await fetch(`${API}/email/campaigns/${activateDialog.id}/activate`, { method: 'POST', credentials: 'include' });
    const data = await res.json();
    setActivating(false);
    setActivateDialog(null);
    fetchCampaigns(); fetchOverview();
  };

  const deleteCampaign = async (id) => {
    await fetch(`${API}/email/campaigns/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchCampaigns(); fetchOverview();
  };

  const generateContent = async () => {
    setGenerating(true); setGeneratedContent(null);
    try {
      const res = await fetch(`${API}/email/templates/ai-generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language: genLanguage, step_name: genStep, segment: genSegment,
          facility_name: genFacilityName, facility_city: genCity,
          contact_name: genContactName, extra_info: genExtraInfo,
        }),
      });
      setGeneratedContent(await res.json());
    } catch { /* ignore */ }
    setGenerating(false);
  };

  const generateFullSequence = async () => {
    setGeneratingSeq(true); setGeneratedSequence(null);
    try {
      const res = await fetch(`${API}/email/templates/ai-generate-sequence`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language: genLanguage, segment: genSegment,
          facility_name: genFacilityName, facility_city: genCity,
          contact_name: genContactName, extra_info: genExtraInfo,
        }),
      });
      const data = await res.json();
      setGeneratedSequence(data.sequence || []);
    } catch { /* ignore */ }
    setGeneratingSeq(false);
  };

  const previewSegment = async () => {
    const res = await fetch(`${API}/email/segments/count`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(segmentFilter),
    });
    const data = await res.json();
    setSegmentCount(data.count);
  };

  const [campaignSegmentCount, setCampaignSegmentCount] = useState(null);
  const [profileDimensions, setProfileDimensions] = useState([]);

  // Profiel-dimensies laden bij open van create form. Komt uit
  // `profile.facility_filter_dimensions` (lijst van {key, label, options:[{value,label}]}).
  // Wordt gebruikt om dynamische segment-filters te tonen zoals "Bedrijfstype: manegebedrijf".
  useEffect(() => {
    let alive = true;
    fetch(`${API}/profiles/filter-config`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!alive || !d) return;
        const dims = d.facility_filter_dimensions || [];
        setProfileDimensions(Array.isArray(dims) ? dims : []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const toggleDimensionValue = (dimKey, value) => {
    setNewCampaign(c => {
      const sf = { ...(c.segment_filter || {}) };
      const dims = { ...(sf.dimensions || {}) };
      const cur = Array.isArray(dims[dimKey]) ? [...dims[dimKey]] : [];
      const idx = cur.indexOf(value);
      if (idx >= 0) cur.splice(idx, 1); else cur.push(value);
      if (cur.length) dims[dimKey] = cur; else delete dims[dimKey];
      if (Object.keys(dims).length) sf.dimensions = dims; else delete sf.dimensions;
      return { ...c, segment_filter: sf };
    });
  };
  useEffect(() => {
    if (!showCreate) return;
    const f = newCampaign.segment_filter || {};
    let cancelled = false;
    fetch(`${API}/email/segments/count`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }).then(r => r.json()).then(d => { if (!cancelled) setCampaignSegmentCount(d.count); }).catch(() => {});
    return () => { cancelled = true; };
  }, [showCreate, newCampaign.segment_filter]);

  // Load mailboxes when create form opens; pre-select the default sender.
  useEffect(() => {
    if (!showCreate) return;
    fetch(`${API}/marketing/mailboxes`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const list = d.mailboxes || [];
        setMailboxes(list);
        if (!newCampaign.sender_email && list.length > 0) {
          const def = list.find(m => m.is_default) || list[0];
          setNewCampaign(c => ({
            ...c,
            sender_email: def.email,
          }));
        }
      })
      .catch(() => {});
     
  }, [showCreate]);

  const handleMailboxChange = (email) => {
    const m = mailboxes.find(x => x.email === email);
    setNewCampaign(c => ({
      ...c,
      sender_email: email,
      // Reset any custom signature override when picking a new mailbox so the
      // mailbox's own signature is auto-applied at send-time.
      signature_html: '',
      // Keep sender_name in sync (user can still override later via PUT).
      sender_name: m?.from_name || c.sender_name,
    }));
  };

  const sendTestMail = async () => {
    if (!newCampaign.sender_email || !testMail.to) return;
    setTestMail(t => ({ ...t, sending: true, result: null }));
    try {
      const r = await fetch(`${API}/marketing/test-send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_email: newCampaign.sender_email,
          to_email: testMail.to,
          signature_html: newCampaign.signature_html || undefined,
          subject: `[TEST] ${newCampaign.name || 'PoolFIX campagne preview'}`,
          body_html: `<p>Beste ontvanger,</p><p>Dit is een testmail van PoolFIX om de gekozen afzender (<strong>${newCampaign.sender_email}</strong>) en handtekening te verifiëren vóór de campagne wordt geactiveerd.</p>`,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.status === 'sent') {
        setTestMail(t => ({ ...t, sending: false, result: { ok: true, msg: `Testmail verzonden naar ${data.to} — controleer je inbox` } }));
      } else {
        setTestMail(t => ({ ...t, sending: false, result: { ok: false, msg: data.detail || 'Verzenden mislukt' } }));
      }
    } catch {
      setTestMail(t => ({ ...t, sending: false, result: { ok: false, msg: 'Verbindingsfout' } }));
    }
  };

  const togglePoolCategory = (cat) => {
    setNewCampaign(c => {
      const cur = Array.isArray(c.segment_filter?.pool_category) ? [...c.segment_filter.pool_category] : (c.segment_filter?.pool_category ? [c.segment_filter.pool_category] : []);
      const idx = cur.indexOf(cat);
      if (idx >= 0) cur.splice(idx, 1); else cur.push(cat);
      const sf = { ...(c.segment_filter || {}) };
      if (cur.length === 0) delete sf.pool_category; else sf.pool_category = cur;
      return { ...c, segment_filter: sf };
    });
  };

  const toggleLeadType = (lt) => {
    setNewCampaign(c => {
      const cur = Array.isArray(c.segment_filter?.lead_type) ? [...c.segment_filter.lead_type] : (c.segment_filter?.lead_type ? [c.segment_filter.lead_type] : []);
      const idx = cur.indexOf(lt);
      if (idx >= 0) cur.splice(idx, 1); else cur.push(lt);
      const sf = { ...(c.segment_filter || {}) };
      if (cur.length === 0) delete sf.lead_type; else sf.lead_type = cur;
      return { ...c, segment_filter: sf };
    });
  };

  const toggleCampaignCountry = (code) => {
    setNewCampaign(c => {
      const cur = Array.isArray(c.segment_filter?.country_code) ? [...c.segment_filter.country_code] : (c.segment_filter?.country_code ? [c.segment_filter.country_code] : []);
      const idx = cur.indexOf(code);
      if (idx >= 0) cur.splice(idx, 1); else cur.push(code);
      const sf = { ...(c.segment_filter || {}) };
      if (cur.length === 0) delete sf.country_code; else sf.country_code = cur;
      return { ...c, segment_filter: sf };
    });
  };

  const simulateBatch = async () => {
    setSimulating(true);
    try {
      await fetch(`${API}/email/inbox/simulate-batch`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 5 }),
      });
      fetchInbox(); fetchInboxStats(); fetchOverview();
    } catch { /* ignore */ }
    setSimulating(false);
  };

  // A/B Test actions
  const createAbTest = async () => {
    setAbCreating(true);
    try {
      await fetch(`${API}/email/ab-tests`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(abForm),
      });
      fetchAbTests(); fetchAbStats(); setShowCreateAB(false);
      setAbForm({ name: '', dimension: 'subject', winning_metric: 'open_rate', variant_a: { label: 'Variant A', subject: '', text_body: '', html_body: '' }, variant_b: { label: 'Variant B', subject: '', text_body: '', html_body: '' } });
    } catch { /* ignore */ }
    setAbCreating(false);
  };

  const abTestAction = async (testId, action) => {
    setAbActionLoading(`${testId}_${action}`);
    try {
      await fetch(`${API}/email/ab-tests/${testId}/${action}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      fetchAbTests(); fetchAbStats();
    } catch { /* ignore */ }
    setAbActionLoading('');
  };

  const deleteAbTest = async (testId) => {
    await fetch(`${API}/email/ab-tests/${testId}`, { method: 'DELETE', credentials: 'include' });
    fetchAbTests(); fetchAbStats();
  };

  const aiGenerateVariants = async () => {
    setAiGeneratingVariants(true);
    try {
      const res = await fetch(`${API}/email/ab-tests/ai-generate-variants`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dimension: abForm.dimension, language: genLanguage, segment: genSegment }),
      });
      const data = await res.json();
      if (abForm.dimension === 'subject') {
        setAbForm(f => ({
          ...f,
          variant_a: { ...f.variant_a, label: data.variant_a?.label || 'Variant A', subject: data.variant_a?.subject || '' },
          variant_b: { ...f.variant_b, label: data.variant_b?.label || 'Variant B', subject: data.variant_b?.subject || '' },
        }));
      } else {
        setAbForm(f => ({
          ...f,
          variant_a: { ...f.variant_a, label: data.variant_a?.label || 'Variant A', text_body: data.variant_a?.text_body || '', html_body: data.variant_a?.html_body || '' },
          variant_b: { ...f.variant_b, label: data.variant_b?.label || 'Variant B', text_body: data.variant_b?.text_body || '', html_body: data.variant_b?.html_body || '' },
        }));
      }
    } catch { /* ignore */ }
    setAiGeneratingVariants(false);
  };

  // Auto-Optimization actions
  const fetchOptInsights = useCallback(() => {
    fetch(`${API}/email/optimize/insights`, { credentials: 'include' })
      .then(r => r.json()).then(setOptInsights).catch(() => {});
  }, []);

  const fetchOptHistory = useCallback(() => {
    fetch(`${API}/email/optimize/history?limit=20`, { credentials: 'include' })
      .then(r => r.json()).then(d => setOptHistory(d.history || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchOptInsights(); fetchOptHistory(); }, [fetchOptInsights, fetchOptHistory]);

  const runSingleOptimization = async () => {
    setOptRunning(true);
    try {
      await fetch(`${API}/email/optimize/run`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      fetchOptInsights(); fetchOptHistory(); fetchAbTests(); fetchAbStats();
    } catch { /* ignore */ }
    setOptRunning(false);
  };

  const runBatchOptimization = async () => {
    setOptBatchRunning(true);
    try {
      await fetch(`${API}/email/optimize/run-batch`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 3 }),
      });
      fetchOptInsights(); fetchOptHistory(); fetchAbTests(); fetchAbStats();
    } catch { /* ignore */ }
    setOptBatchRunning(false);
  };

  const loadAiSummary = async () => {
    setAiSummaryLoading(true);
    try {
      const res = await fetch(`${API}/email/optimize/ai-summary`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      setAiSummary(await res.json());
    } catch { /* ignore */ }
    setAiSummaryLoading(false);
  };

  // Live Preview
  const [livePreview, setLivePreview] = useState(null);
  const [livePreviewLoading, setLivePreviewLoading] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);

  const fetchQueueStatus = useCallback(() => {
    fetch(`${API}/email/queue/status`, { credentials: 'include' })
      .then(r => r.json()).then(setQueueStatus).catch(() => {});
  }, []);

  useEffect(() => { fetchQueueStatus(); }, [fetchQueueStatus]);

  const loadLivePreview = async (step = 1) => {
    setLivePreviewLoading(true);
    setLivePreview(null);
    try {
      const res = await fetch(`${API}/email/live-preview`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step }),
      });
      const data = await res.json();
      if (data.detail || data.error) {
        setLivePreview({ error: data.detail || data.error });
      } else {
        setLivePreview(data);
      }
    } catch (e) {
      setLivePreview({ error: 'Verbinding mislukt. Probeer opnieuw.' });
    }
    setLivePreviewLoading(false);
  };

  const processQueue = async () => {
    await fetch(`${API}/email/queue/process`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit: 10 }),
    });
    fetchQueueStatus();
    fetchOverview();
  };

  const toggleWorker = async () => {
    const action = queueStatus?.worker?.running ? 'stop' : 'start';
    await fetch(`${API}/email/queue/worker/${action}`, {
      method: 'POST', credentials: 'include',
    });
    fetchQueueStatus();
  };

  const [cleanupResult, setCleanupResult] = useState(null);
  const cleanupQueue = async () => {
    const res = await fetch(`${API}/email/queue/cleanup`, {
      method: 'POST', credentials: 'include',
    });
    const data = await res.json();
    setCleanupResult(data);
    fetchQueueStatus();
    fetchOverview();
  };

  const pendingCount = inboxStats ? (inboxStats.wacht_op_goedkeuring || 0) : 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="email-campaigns-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Marketing</h1>
          <p className="text-sm text-zinc-500 mt-1">Campagnes, templates, AI content en inkomende e-mail verwerking</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="open-library-btn"
            onClick={() => setShowLibrary(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white text-amber-700 border border-amber-300 rounded-sm hover:bg-amber-50 transition-colors"
            title="Browse coach-validated sequences (score 80+) van alle profielen"
          >
            <Star size={14} weight="fill" className="text-amber-500" /> Library
          </button>
          <button data-testid="create-campaign-btn" onClick={async () => {
            setShowCreate(true);
            try {
              const r = await fetch(`${API}/campaign-templates`, { credentials: 'include' });
              const d = await r.json();
              setCampaignTemplates(d.templates || []);
            } catch { /* ignore */ }
          }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors">
            <Plus size={14} weight="bold" /> Nieuwe Campagne
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="email-stats">
          {[
            { l: 'Campagnes', v: overview.total_campaigns, c: 'text-zinc-700', i: PaperPlaneTilt },
            { l: 'Actief', v: overview.active_campaigns, c: 'text-emerald-600', i: Play },
            { l: 'Verzonden', v: overview.total_sent, c: 'text-blue-600', i: EnvelopeSimple },
            { l: 'Geopend', v: overview.total_opens, c: 'text-cyan-600', i: EnvelopeOpen },
            { l: 'Geklikt', v: overview.total_clicks, c: 'text-indigo-600', i: CursorClick },
            { l: 'Open Rate', v: `${overview.open_rate}%`, c: 'text-emerald-600', i: ChartBar },
            { l: 'Click Rate', v: `${overview.click_rate}%`, c: 'text-blue-600', i: CursorClick },
            { l: 'Inbox', v: overview.pending_replies, c: overview.pending_replies > 0 ? 'text-amber-600' : 'text-zinc-400', i: ChatCircle },
          ].map((s, i) => (
            <div key={i} className="bg-white p-3 text-center">
              <s.i size={16} className="mx-auto mb-1 text-zinc-400" />
              <div className={`font-data text-lg font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sequence Health Dashboard */}
      {healthData && healthData.summary && healthData.summary.total > 0 && (
        <div className="mb-6 rounded-md border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-cyan-50 to-violet-50 p-4" data-testid="sequence-health-dashboard">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xl">💚</span>
              <h3 className="text-sm font-bold text-emerald-900 uppercase tracking-wide">Sequence Health</h3>
              <span className="text-[10px] text-zinc-500">één getal per campagne — combineert Coach, Predictor, per-step actuals, calibration & adoption</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-zinc-500">Gem.</span>
              <span className={`font-data font-bold text-xl ${healthData.summary.avg_score >= 80 ? 'text-emerald-700' : healthData.summary.avg_score >= 60 ? 'text-amber-700' : 'text-rose-700'}`} data-testid="health-avg-score">
                {healthData.summary.avg_score ?? '–'}
              </span>
              <button
                onClick={fetchHealth}
                disabled={healthLoading}
                className="ml-2 text-[10px] text-emerald-700 hover:text-emerald-900 underline"
                data-testid="health-refresh-btn"
              >
                {healthLoading ? 'laden...' : 'ververs'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <button
              onClick={() => setShowOnlyAttention(false)}
              className={`px-2 py-1.5 rounded text-center transition-colors ${!showOnlyAttention ? 'bg-white border-2 border-emerald-400' : 'bg-white/50 border border-zinc-200 hover:bg-white'}`}
              data-testid="health-filter-all"
            >
              <div className="text-[9px] uppercase text-zinc-500">Healthy</div>
              <div className="font-data font-bold text-emerald-700 text-base">{healthData.summary.healthy}</div>
            </button>
            <div className="px-2 py-1.5 rounded text-center bg-white/50 border border-amber-200">
              <div className="text-[9px] uppercase text-zinc-500">Caution</div>
              <div className="font-data font-bold text-amber-700 text-base">{healthData.summary.caution}</div>
            </div>
            <button
              onClick={() => setShowOnlyAttention(true)}
              className={`px-2 py-1.5 rounded text-center transition-colors ${showOnlyAttention ? 'bg-white border-2 border-rose-400' : 'bg-white/50 border border-rose-200 hover:bg-white'}`}
              data-testid="health-filter-attention"
            >
              <div className="text-[9px] uppercase text-zinc-500">Aandacht nodig</div>
              <div className="font-data font-bold text-rose-700 text-base">{healthData.summary.needs_attention}</div>
            </button>
          </div>
          <div className="space-y-1.5">
            {(healthData.campaigns || [])
              .filter((c) => !showOnlyAttention || c.verdict === 'needs_attention')
              .slice(0, 8)
              .map((c) => {
                const badge = {
                  healthy: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: '💚 Healthy' },
                  caution: { bg: 'bg-amber-100', text: 'text-amber-800', label: '⚠ Caution' },
                  needs_attention: { bg: 'bg-rose-100', text: 'text-rose-800', label: '🚨 Attention' },
                }[c.verdict] || { bg: 'bg-zinc-100', text: 'text-zinc-700', label: '–' };
                return (
                  <div key={c.campaign_id} className="flex items-center gap-2 px-3 py-2 rounded bg-white border border-zinc-200" data-testid={`health-row-${c.campaign_id}`}>
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-data font-bold text-sm ${c.score >= 80 ? 'bg-emerald-500 text-white' : c.score >= 60 ? 'bg-amber-500 text-white' : 'bg-rose-500 text-white'}`}>
                      {Math.round(c.score)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-zinc-900 text-sm truncate">{c.name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide ${badge.bg} ${badge.text}`}>{badge.label}</span>
                      </div>
                      {(c.reasons || []).length > 0 && (
                        <p className="text-[10px] text-zinc-600 truncate mt-0.5" title={c.reasons.join(' · ')}>
                          {c.reasons[0]}
                        </p>
                      )}
                    </div>
                    {(c.weak_steps || []).length > 0 && (
                      <span className="flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 font-data font-bold" title={`Stappen ${c.weak_steps.join(', ')} presteren onder voorspelling`}>
                        ⚠ {c.weak_steps.length} weak
                      </span>
                    )}
                  </div>
                );
              })}
            {showOnlyAttention && (healthData.campaigns || []).filter((c) => c.verdict === 'needs_attention').length === 0 && (
              <p className="text-[11px] text-emerald-700 italic text-center py-2">🎉 Geen campagnes met aandacht nodig — alles op groen.</p>
            )}
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start border-b border-zinc-200 rounded-none bg-transparent p-0 h-auto mb-6">
          {['campaigns', 'overview', 'templates', 'analytics', 'ai-content', 'ab-tests', 'optimization', 'segments', 'settings'].map(tab => (
            <TabsTrigger key={tab} value={tab} data-testid={`tab-${tab}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F172A] data-[state=active]:font-semibold data-[state=active]:shadow-none px-5 py-3 text-sm">
              {tab === 'campaigns' ? 'Campagnes' : tab === 'overview' ? '📋 Overzicht' : tab === 'templates' ? 'Templates' : tab === 'analytics' ? 'Analytics' : tab === 'ai-content' ? 'AI Content' : tab === 'ab-tests' ? 'A/B Tests' : tab === 'optimization' ? 'Optimalisatie' : tab === 'segments' ? 'Segmenten' :
               tab === 'settings' ? 'Instellingen' : tab}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* CAMPAIGNS TAB */}
        <TabsContent value="campaigns" className="mt-0">
          {showCreate && (
            <div className="border border-[#0055FF] bg-blue-50 p-5 mb-4" data-testid="create-campaign-form">
              <h3 className="font-heading text-lg font-semibold mb-3">Nieuwe Campagne</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Naam</label>
                  <Input data-testid="campaign-name" value={newCampaign.name} onChange={e => setNewCampaign(c => ({ ...c, name: e.target.value }))} placeholder="Bijv: Q2 2026 - RVS Awareness NL" className="rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Type</label>
                  <Select value={newCampaign.type} onValueChange={v => setNewCampaign(c => ({ ...c, type: v }))}>
                    <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sequence">Sequence (6-staps)</SelectItem>
                      <SelectItem value="broadcast">Broadcast (eenmalig)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {newCampaign.type === 'sequence' && (
                <div className="mb-3">
                  <label className="text-xs font-medium text-zinc-500 mb-1 block flex items-center gap-2">
                    Start vanaf template
                    <span className="text-[10px] text-zinc-400">(optioneel — kopieert product + 6 stappen + filter)</span>
                    <a
                      href="#templates"
                      onClick={(e) => { e.preventDefault(); setActiveTab('templates'); setShowCreate(false); }}
                      className="ml-auto text-[11px] text-[#0055FF] hover:underline"
                    >
                      + Nieuwe template
                    </a>
                  </label>
                  <Select
                    value={newCampaign.template_id || 'blank'}
                    onValueChange={v => setNewCampaign(c => ({ ...c, template_id: v === 'blank' ? null : v }))}
                  >
                    <SelectTrigger className="rounded-sm text-sm" data-testid="campaign-template-select">
                      <SelectValue placeholder="Kies template of leeg starten..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blank">Leeg starten (geen template)</SelectItem>
                      {campaignTemplates.map((t) => (
                        <SelectItem key={t.template_id} value={t.template_id} data-testid={`template-option-${t.template_id}`}>
                          {t.name}
                          {t.product ? <span className="text-zinc-400"> — {t.product}</span> : null}
                          <span className="ml-2 text-[10px] text-zinc-400">({(t.sequence_steps || []).length} stappen · gebruikt {t.use_count || 0}×)</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newCampaign.template_id && (
                    <p className="text-[11px] text-zinc-500 mt-1 italic">
                      Na aanmaken opent automatisch de Product modal — daar kun je per stap nog de focus aanpassen voor deze campagne.
                    </p>
                  )}
                </div>
              )}

              {/* Sender mailbox + signature override */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-testid="campaign-sender-row">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">
                    Verzendadres
                    <a href="/settings/mailboxes" className="ml-2 text-[11px] text-[#0055FF] hover:underline">+ beheren</a>
                  </label>
                  <Select value={newCampaign.sender_email} onValueChange={handleMailboxChange}>
                    <SelectTrigger className="rounded-sm text-sm" data-testid="campaign-mailbox-trigger">
                      <SelectValue placeholder="Kies afzender..." />
                    </SelectTrigger>
                    <SelectContent>
                      {mailboxes.map(m => (
                        <SelectItem key={m.mailbox_id} value={m.email} data-testid={`mailbox-option-${m.email}`}>
                          <span className="font-medium">{m.email}</span>
                          {m.is_default && <span className="ml-2 text-[10px] text-amber-700">(standaard)</span>}
                          {m.display_name && <span className="ml-1 text-[11px] text-zinc-500">— {m.display_name}</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newCampaign.sender_email && (
                    <p className="text-[11px] text-zinc-400 mt-1" data-testid="campaign-sender-hint">
                      Handtekening van <strong>{newCampaign.sender_email}</strong> wordt automatisch toegevoegd
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">
                    Aangepaste handtekening
                    <span className="text-[10px] text-zinc-400 ml-1">(optioneel — overschrijft mailbox-handtekening)</span>
                  </label>
                  <textarea
                    data-testid="campaign-signature-override"
                    value={newCampaign.signature_html || ''}
                    onChange={e => setNewCampaign(c => ({ ...c, signature_html: e.target.value }))}
                    rows={3}
                    placeholder="Laat leeg voor standaard handtekening"
                    className="w-full px-3 py-2 text-xs font-mono border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A] resize-none"
                  />
                </div>
              </div>

              {/* Test-mail panel */}
              <div className="mb-3 p-3 border border-dashed border-emerald-300 bg-emerald-50/40 rounded-sm" data-testid="test-mail-panel">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs text-zinc-700">
                    <strong>Stuur testmail</strong>
                    <span className="text-zinc-500 ml-2">— ontvang een echte preview van afzender + handtekening voordat je de campagne activeert</span>
                  </div>
                  {!testMail.open ? (
                    <button
                      type="button"
                      data-testid="open-test-mail-btn"
                      onClick={() => setTestMail({ open: true, to: '', sending: false, result: null })}
                      disabled={!newCampaign.sender_email}
                      className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-40 transition-colors"
                    >
                      Stuur testmail
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        data-testid="test-mail-to-input"
                        type="email"
                        placeholder="ontvanger@email.com"
                        value={testMail.to}
                        onChange={e => setTestMail(t => ({ ...t, to: e.target.value, result: null }))}
                        className="px-3 py-1.5 text-xs border border-zinc-200 rounded-sm focus:outline-none focus:border-emerald-500 w-56"
                      />
                      <button
                        type="button"
                        data-testid="send-test-mail-btn"
                        onClick={sendTestMail}
                        disabled={testMail.sending || !testMail.to || !newCampaign.sender_email}
                        className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-40 transition-colors"
                      >
                        {testMail.sending ? 'Bezig...' : 'Verstuur'}
                      </button>
                      <button
                        type="button"
                        data-testid="cancel-test-mail-btn"
                        onClick={() => setTestMail({ open: false, to: '', sending: false, result: null })}
                        className="px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                {testMail.result && (
                  <div
                    data-testid="test-mail-result"
                    className={`mt-2 px-3 py-2 text-xs rounded-sm border ${testMail.result.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}
                  >
                    {testMail.result.msg}
                  </div>
                )}
              </div>
              {/* Generate on Send toggle (sequence only) */}
              {newCampaign.type === 'sequence' && (
                <div className="flex items-center gap-3 mb-3 p-3 border border-dashed border-zinc-300 rounded-sm bg-zinc-50">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="generate-on-send-toggle">
                    <input type="checkbox" checked={newCampaign.generate_on_send} onChange={e => setNewCampaign(c => ({ ...c, generate_on_send: e.target.checked }))}
                      className="w-4 h-4 rounded border-zinc-300 text-[#0055FF] focus:ring-[#0055FF]" />
                    <span className="text-sm font-medium">Generate on Send</span>
                  </label>
                  <span className="text-xs text-zinc-500">Emails worden pas op het moment van verzending gegenereerd met live weer- en nieuwsdata</span>
                  {newCampaign.generate_on_send && <Badge className="rounded-sm px-2 py-0.5 text-[10px] bg-cyan-100 text-cyan-800 border border-cyan-200">Live AI</Badge>}
                </div>
              )}
              {newCampaign.type === 'broadcast' && (
                <div className="flex items-center gap-3 mb-3 p-3 border border-dashed border-purple-300 rounded-sm bg-purple-50/40">
                  <Badge className="rounded-sm px-2 py-0.5 text-[10px] bg-purple-100 text-purple-700 border border-purple-200">Eenmalig</Badge>
                  <span className="text-xs text-zinc-600">Eenmalige broadcast — beschrijf de boodschap, AI schrijft de email, jij keurt goed en verzendt naar de hele doelgroep.</span>
                </div>
              )}

              {/* Doelgroep filters */}
              <div className="border border-zinc-200 bg-white p-4 mb-3" data-testid="campaign-target-filters">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Funnel size={14} className="text-[#0055FF]" />
                    <h4 className="text-sm font-semibold">Doelgroep</h4>
                    <span className="text-xs text-zinc-400">— bepaal welke contacten deze campagne ontvangen</span>
                  </div>
                  <div className="text-xs">
                    <span className="font-data text-base font-bold text-[#0055FF]" data-testid="campaign-segment-count">
                      {campaignSegmentCount === null ? '...' : campaignSegmentCount}
                    </span>
                    <span className="text-zinc-500 ml-1">contacten</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Land */}
                  <div>
                    <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Land</label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { code: 'NL', label: 'Nederland' },
                        { code: 'BE', label: 'Belgie' },
                        { code: 'FR', label: 'Frankrijk' },
                        { code: 'DE', label: 'Duitsland' },
                      ].map(c => {
                        const sel = (newCampaign.segment_filter?.country_code || []).includes?.(c.code) || newCampaign.segment_filter?.country_code === c.code;
                        return (
                          <button
                            key={c.code}
                            type="button"
                            onClick={() => toggleCampaignCountry(c.code)}
                            data-testid={`segment-country-${c.code}`}
                            className={`px-2.5 py-1 text-xs border transition-colors ${sel ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}`}
                          >
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-1.5">Geen selectie = alle landen</p>
                  </div>

                  {/* Type zwembad */}
                  <div>
                    <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Type zwembad</label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { v: 'indoor', label: 'Overdekt' },
                        { v: 'outdoor', label: 'Buiten' },
                      ].map(p => {
                        const sel = (newCampaign.segment_filter?.pool_category || []).includes?.(p.v) || newCampaign.segment_filter?.pool_category === p.v;
                        return (
                          <button
                            key={p.v}
                            type="button"
                            onClick={() => togglePoolCategory(p.v)}
                            data-testid={`segment-pool-${p.v}`}
                            className={`px-2.5 py-1 text-xs border transition-colors ${sel ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}`}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-1.5">Beide aan = overdekt EN buiten; geen selectie = alle types (incl. niet-zwembad)</p>
                  </div>

                  {/* Lead type */}
                  <div>
                    <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Doelgroep type</label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { v: 'pool_facility', label: 'Zwembaden' },
                        { v: 'installer', label: 'Installateurs' },
                        { v: 'wholesaler', label: 'Groothandels' },
                        { v: 'supplier', label: 'Toeleveranciers' },
                        { v: 'distributor', label: 'Distributeurs' },
                      ].map(t => {
                        const sel = (newCampaign.segment_filter?.lead_type || []).includes?.(t.v) || newCampaign.segment_filter?.lead_type === t.v;
                        return (
                          <button
                            key={t.v}
                            type="button"
                            onClick={() => toggleLeadType(t.v)}
                            data-testid={`segment-leadtype-${t.v}`}
                            className={`px-2.5 py-1 text-xs border transition-colors ${sel ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}`}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-1.5">Geen selectie = alle types</p>
                  </div>
                </div>

                {/* Profile-specifieke dimensies (AI-classificatie) */}
                {profileDimensions.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-zinc-100 space-y-3" data-testid="campaign-dimension-filters">
                    <div className="flex items-center gap-2">
                      <Funnel size={12} className="text-purple-600" />
                      <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Geclassificeerde organisatietypes</label>
                      <span className="text-[10px] text-zinc-400">— AI-classificatie van de faciliteit</span>
                    </div>
                    {profileDimensions.map(dim => {
                      const selected = newCampaign.segment_filter?.dimensions?.[dim.key] || [];
                      const selectedSet = new Set(Array.isArray(selected) ? selected : [selected]);
                      return (
                        <div key={dim.key} data-testid={`dim-block-${dim.key}`}>
                          <div className="text-[10px] font-medium text-zinc-600 mb-1.5">{dim.label || dim.key}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(dim.options || []).map(opt => {
                              const isSel = selectedSet.has(opt.value);
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => toggleDimensionValue(dim.key, opt.value)}
                                  data-testid={`segment-dim-${dim.key}-${opt.value}`}
                                  className={`px-2.5 py-1 text-xs border transition-colors ${isSel ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-purple-50'}`}
                                >
                                  {opt.label || opt.value}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-[10px] text-zinc-400 mt-1">Geen selectie binnen een rij = die dimensie wordt niet meegefilterd</p>
                  </div>
                )}

                {/* Customer status row */}
                <div className="mt-4 pt-4 border-t border-zinc-100">
                  <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Klantrelatie</label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { v: 'active_customer', label: 'Bestaande klanten', cls: 'border-emerald-300 text-emerald-700 bg-emerald-50', sel: 'bg-emerald-600 text-white border-emerald-600' },
                      { v: 'hot_lead', label: 'Hot leads', cls: 'border-red-300 text-red-700 bg-red-50', sel: 'bg-red-600 text-white border-red-600' },
                      { v: 'lead', label: 'Open leads', cls: 'border-zinc-300 text-zinc-700 bg-white', sel: 'bg-[#0055FF] text-white border-[#0055FF]' },
                    ].map(t => {
                      const isSel = newCampaign.segment_filter?.customer_status === t.v;
                      return (
                        <button
                          key={t.v}
                          type="button"
                          onClick={() => setNewCampaign(c => {
                            const sf = { ...(c.segment_filter || {}) };
                            if (sf.customer_status === t.v) delete sf.customer_status;
                            else sf.customer_status = t.v;
                            return { ...c, segment_filter: sf };
                          })}
                          data-testid={`segment-customer-${t.v}`}
                          className={`px-2.5 py-1 text-xs border transition-colors ${isSel ? t.sel : t.cls + ' hover:opacity-80'}`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-1.5">Geen selectie = iedereen (klant of lead). "Bestaande klanten" stuurt alleen naar contacten met `customer_status = active_customer`.</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={createCampaign} disabled={creating || !newCampaign.name}
                  className="px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                  {creating ? 'Aanmaken...' : 'Campagne Aanmaken'}
                </button>
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors">Annuleren</button>
              </div>
            </div>
          )}

          <div className="border border-zinc-200 overflow-hidden" data-testid="campaigns-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Campagne</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Type</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Status</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Verzonden</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Opens</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Clicks</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Replies</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Acties</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-400 text-xs">Geen campagnes. Maak een nieuwe campagne aan.</td></tr>
                ) : campaigns.map(c => (
                  <tr key={c.campaign_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{c.name}</span>
                        {c.generate_on_send && <Badge className="rounded-sm px-1.5 py-0.5 text-[9px] bg-cyan-100 text-cyan-800 border border-cyan-200">Live AI</Badge>}
                      </div>
                      <div className="text-xs text-zinc-400">{c.campaign_id}</div>
                    </td>
                    <td className="px-4 py-3"><Badge className="rounded-full px-2 py-0.5 text-xs font-data bg-zinc-100 text-zinc-600">{c.type}</Badge></td>
                    <td className="px-4 py-3"><Badge className={`rounded-full px-2 py-0.5 text-xs font-data ${STATUS_COLORS[c.status] || 'bg-zinc-100'}`}>{c.status}</Badge></td>
                    <td className="px-4 py-3 text-center font-data">{c.stats?.emails_sent || 0}</td>
                    <td className="px-4 py-3 text-center font-data">{c.stats?.unique_opens || 0}</td>
                    <td className="px-4 py-3 text-center font-data">{c.stats?.unique_clicks || 0}</td>
                    <td className="px-4 py-3 text-center font-data">{c.stats?.replies || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {c.type === 'broadcast' && c.broadcast_status === 'sent' && (
                          <button onClick={() => setBroadcastStatsId(c.campaign_id)} className="p-1.5 hover:bg-emerald-50 rounded-sm" title="Statistieken" data-testid={`broadcast-stats-${c.campaign_id}`}>
                            <ChartBar size={14} className="text-emerald-600" />
                          </button>
                        )}
                        {c.type === 'broadcast' && c.broadcast_status !== 'sent' && (
                          <button onClick={() => setBroadcastCampaign(c)} className="p-1.5 hover:bg-purple-50 rounded-sm" title="Broadcast bewerken" data-testid={`broadcast-edit-${c.campaign_id}`}>
                            <PaperPlaneRight size={14} className="text-purple-600" />
                          </button>
                        )}
                        {c.type === 'sequence' && c.status === 'draft' && <button onClick={() => confirmActivate(c.campaign_id)} className="p-1.5 hover:bg-emerald-50 rounded-sm" title="Activeren"><Play size={14} className="text-emerald-600" /></button>}
                        {c.type === 'sequence' && (c.status === 'active' || c.status === 'paused' || c.status === 'completed') && (
                          <button
                            onClick={() => setQueueCampaign(c)}
                            data-testid={`campaign-queue-${c.campaign_id}`}
                            className="p-1.5 hover:bg-indigo-50 rounded-sm"
                            title="Bekijk wie wanneer welke stap krijgt — handmatig contacten uit de loop halen"
                          >
                            <ListBullets size={14} className="text-indigo-600" />
                          </button>
                        )}
                        {c.type === 'sequence' && (c.status === 'active' || c.status === 'paused') && (
                          <button
                            onClick={() => setAddContactsCampaign(c)}
                            data-testid={`campaign-add-contacts-${c.campaign_id}`}
                            className="p-1.5 hover:bg-emerald-50 rounded-sm"
                            title="Voeg nieuwe contacten (die nu in segment vallen) toe aan deze lopende campagne"
                          >
                            <UserPlus size={14} className="text-emerald-600" />
                          </button>
                        )}
                        {c.type === 'sequence' && <button onClick={() => setProductModalCampaign(c)} className="p-1.5 hover:bg-purple-50 rounded-sm" title="Product & Sequence" data-testid={`campaign-product-btn-${c.campaign_id}`}><Lightning size={14} className="text-purple-600" /></button>}
                        {c.status === 'active' && <button onClick={() => campaignAction(c.campaign_id, 'pause')} className="p-1.5 hover:bg-amber-50 rounded-sm" title="Pauzeren"><Pause size={14} className="text-amber-600" /></button>}
                        {c.status === 'paused' && <button onClick={() => campaignAction(c.campaign_id, 'activate')} className="p-1.5 hover:bg-emerald-50 rounded-sm" title="Hervatten"><Play size={14} className="text-emerald-600" /></button>}
                        {c.status !== 'stopped' && c.status !== 'completed' && c.type !== 'broadcast' && <button onClick={() => campaignAction(c.campaign_id, 'stop')} className="p-1.5 hover:bg-red-50 rounded-sm" title="Stoppen"><Stop size={14} className="text-red-500" /></button>}
                        <button
                          onClick={() => setEditCampaign(c)}
                          className="p-1.5 hover:bg-blue-50 rounded-sm"
                          title="Bewerk afzender / handtekening"
                          data-testid={`campaign-edit-${c.campaign_id}`}
                        >
                          <PencilSimple size={14} className="text-[#0055FF]" />
                        </button>
                        <button
                          onClick={() => setDuplicateCampaign(c)}
                          className="p-1.5 hover:bg-purple-50 rounded-sm"
                          title="Dupliceer campagne"
                          data-testid={`campaign-duplicate-${c.campaign_id}`}
                        >
                          <Copy size={14} className="text-purple-600" />
                        </button>
                        <button onClick={() => deleteCampaign(c.campaign_id)} className="p-1.5 hover:bg-red-50 rounded-sm" title="Verwijderen"><Trash size={14} className="text-zinc-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Verzonden Broadcasts overzicht */}
          <div className="mt-6">
            <BroadcastsList onOpen={(cid) => setBroadcastStatsId(cid)} />
          </div>

          <div className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <PaperPlaneTilt size={20} weight="bold" className="text-[#0055FF]" /> Email Sequence (6 stappen)
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={aiGenerateSequence}
                  disabled={aiSequenceLoading}
                  data-testid="ai-generate-sequence-btn"
                  title="Genereer een 6-stappen sequence op maat van dit profiel (op basis van marktbeschrijving + zoektermen)"
                  className="text-xs px-2.5 py-1.5 bg-amber-100 text-amber-900 border border-amber-300 rounded-sm hover:bg-amber-200 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {aiSequenceLoading ? '⏳ Genereren...' : '✨ AI sequence genereren'}
                </button>
                <button onClick={() => setEditingSteps(!editingSteps)} className="text-xs text-[#0055FF] hover:underline flex items-center gap-1" data-testid="toggle-edit-steps">
                  <PencilSimple size={12} /> {editingSteps ? 'Sluiten' : 'Focus aanpassen'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stepConfigs.map(s => (
                <div key={s.step} className="border border-zinc-200 p-3 rounded-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0055FF] text-white text-xs font-bold">{s.step}</span>
                    <span className="text-sm font-medium">{s.name}</span>
                    <span className="text-[10px] text-zinc-400 ml-auto">Dag {SEQUENCE_STEPS[s.step - 1]?.delay || 0}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mb-1">{s.goal}</p>
                  {editingSteps ? (
                    <div className="mt-2">
                      <label className="text-[10px] text-zinc-400 uppercase tracking-wider">Technische focus</label>
                      <textarea
                        data-testid={`step-focus-${s.step}`}
                        value={editStepFocus[s.step] ?? s.focus ?? ''}
                        onChange={e => setEditStepFocus(f => ({ ...f, [s.step]: e.target.value }))}
                        className="w-full rounded-sm border border-zinc-300 p-2 text-xs min-h-[60px] focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF] mt-1"
                        placeholder="Beschrijf de technische focus voor deze stap..."
                      />
                      <button onClick={() => saveStepConfig(s.step)} disabled={stepSaving === s.step}
                        className="mt-1 flex items-center gap-1 text-[10px] text-emerald-600 hover:underline" data-testid={`save-step-${s.step}`}>
                        {stepSaving === s.step ? <CircleNotch size={10} className="animate-spin" /> : <Check size={10} />}
                        {stepSaving === s.step ? 'Opslaan...' : 'Opslaan'}
                      </button>
                    </div>
                  ) : (
                    s.focus && <p className="text-[11px] text-blue-700 bg-blue-50 p-1.5 rounded-sm mt-1 line-clamp-2">{s.focus}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Live Preview */}
          <div className="mt-6 border border-cyan-200 bg-cyan-50/30 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Lightning size={18} weight="bold" className="text-cyan-600" />
                <h3 className="font-heading text-base font-semibold">Live Preview — Generate on Send</h3>
                <Badge className="rounded-sm px-2 py-0.5 text-[10px] bg-cyan-100 text-cyan-800 border border-cyan-200">Real-time</Badge>
              </div>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map(s => (
                  <button key={s} data-testid={`live-preview-step-${s}`} onClick={() => loadLivePreview(s)} disabled={livePreviewLoading}
                    className={`w-7 h-7 flex items-center justify-center rounded-sm text-xs font-bold transition-colors ${livePreviewLoading ? 'opacity-50' : 'hover:bg-cyan-200'} ${livePreview?.step === s ? 'bg-cyan-600 text-white' : 'bg-cyan-100 text-cyan-700'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Bekijk hoe een email er <strong>nu</strong> uit zou zien met live weer, nieuws en seizoensdata.</p>

            {livePreviewLoading && (
              <div className="flex items-center gap-2 py-8 justify-center text-sm text-cyan-600">
                <CircleNotch size={16} className="animate-spin" /> Live context ophalen en AI email genereren...
              </div>
            )}

            {livePreview && !livePreviewLoading && (
              livePreview.error ? (
                <div className="text-center py-6 border border-red-200 bg-red-50 text-red-600 text-sm" data-testid="live-preview-error">
                  {livePreview.error}
                  <div className="mt-2"><button onClick={() => loadLivePreview(1)} className="text-xs text-red-500 hover:underline">Opnieuw proberen</button></div>
                </div>
              ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className="rounded-sm px-2 py-0.5 text-xs bg-cyan-100 text-cyan-800">Stap {livePreview.step}: {livePreview.step_name}</Badge>
                  <Badge className="rounded-sm px-2 py-0.5 text-xs bg-zinc-100 text-zinc-600">{livePreview.language}</Badge>
                  {livePreview.language && livePreview.language !== 'nl' && (
                    <TranslateButton text={livePreview.text_body || livePreview.subject} sourceLang={livePreview.language}
                      onTranslated={(t) => setLivePreview(p => ({ ...p, _translated: t }))} size="sm" label="→ NL" />
                  )}
                  {livePreview.realtime_context?.weather && (
                    <span className="text-xs text-zinc-500">{livePreview.realtime_context.weather}</span>
                  )}
                  {livePreview.realtime_context?.news_count > 0 && (
                    <span className="text-xs text-zinc-400">{livePreview.realtime_context.news_count} nieuwsartikelen</span>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500">Onderwerp</label>
                  <div className="text-sm font-medium mt-0.5 p-2 bg-white border border-zinc-200 rounded-sm">{livePreview.subject}</div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-zinc-500">Email (live gegenereerd)</label>
                    {livePreview._translated && <span className="text-[10px] text-indigo-500 cursor-pointer hover:underline" onClick={() => setLivePreview(p => ({ ...p, _translated: '' }))}>Toon origineel</span>}
                  </div>
                  {livePreview._translated ? (
                    <div className="text-sm mt-0.5 p-3 bg-white border border-indigo-200 rounded-sm whitespace-pre-wrap leading-relaxed">{livePreview._translated}</div>
                  ) : (
                    <div className="text-sm mt-0.5 p-3 bg-white border border-zinc-200 rounded-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: livePreview.html_body }} />
                  )}
                </div>
                <div className="text-[10px] text-zinc-400">Gegenereerd op {livePreview.generated_at ? new Date(livePreview.generated_at).toLocaleString('nl-NL') : ''} door {livePreview.generated_by}</div>
              </div>
              )
            )}

            {!livePreview && !livePreviewLoading && (
              <div className="text-center py-6 text-zinc-400 text-xs">Klik op een stap-nummer hierboven om een live preview te genereren.</div>
            )}
          </div>

          {/* Queue Status */}
          {queueStatus && (queueStatus.total > 0 || queueStatus.queued > 0) && (
            <div className="mt-4 border border-zinc-200 bg-white p-4" data-testid="queue-status-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-zinc-500" />
                  <span className="text-sm font-semibold">Email Wachtrij</span>
                  {queueStatus.rate_limit && (
                    <span className="text-[10px] text-zinc-400 bg-zinc-100 px-2 py-0.5" data-testid="rate-limit-badge">{queueStatus.rate_limit}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={cleanupQueue} data-testid="cleanup-queue-btn"
                    className="text-xs px-3 py-1 border border-amber-300 text-amber-600 hover:bg-amber-50">
                    Opschonen
                  </button>
                  <button onClick={toggleWorker} data-testid="toggle-worker-btn"
                    className={`text-xs px-3 py-1 border ${queueStatus.worker?.running ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-emerald-300 text-emerald-600 hover:bg-emerald-50'}`}>
                    {queueStatus.worker?.running ? 'Stop Worker' : 'Start Worker'}
                  </button>
                  <button onClick={processQueue} className="text-xs text-[#0055FF] hover:underline" data-testid="process-queue-btn">Verwerk nu</button>
                </div>
              </div>
              {/* Worker status banner */}
              {cleanupResult && (
                <div className="flex items-center gap-2 text-xs mb-3 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200" data-testid="cleanup-result">
                  <span>Opgeschoond: {cleanupResult.blocked_removed} geblokkeerde emails, {cleanupResult.duplicates_removed} duplicaten verwijderd ({cleanupResult.total_cleaned} totaal)</span>
                  <button onClick={() => setCleanupResult(null)} className="ml-auto text-amber-400 hover:text-amber-600">&times;</button>
                </div>
              )}
              {queueStatus.worker && (
                <div className={`flex items-center gap-2 text-xs mb-3 px-3 py-2 ${queueStatus.worker.running ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-zinc-50 text-zinc-500 border border-zinc-200'}`} data-testid="worker-status-banner">
                  <div className={`w-2 h-2 rounded-full ${queueStatus.worker.running ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-400'}`} />
                  <span className="font-medium">{queueStatus.worker.running ? 'Worker actief' : 'Worker gestopt'}</span>
                  <span className="text-zinc-400">|</span>
                  <span>Max {queueStatus.worker.rate_limit_seconds || 30}s per email</span>
                  {queueStatus.worker.self_learning && (
                    <>
                      <span className="text-zinc-400">|</span>
                      <span className="text-indigo-600 font-medium" data-testid="self-learning-badge">Self-learning A/B actief ({queueStatus.worker.ab_test_ratio} test)</span>
                    </>
                  )}
                  {queueStatus.worker.last_sent_at && (
                    <>
                      <span className="text-zinc-400">|</span>
                      <span>Laatst: {new Date(queueStatus.worker.last_sent_at).toLocaleTimeString('nl-NL')}</span>
                    </>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-center text-xs">
                {[
                  { l: 'Totaal', v: queueStatus.total },
                  { l: 'Wachtend', v: queueStatus.queued, c: 'text-amber-600' },
                  { l: 'Verzonden', v: queueStatus.sent, c: 'text-emerald-600' },
                  { l: 'Mislukt', v: queueStatus.failed, c: 'text-red-500' },
                  { l: 'Live AI', v: queueStatus.live_sent, c: 'text-cyan-600' },
                  { l: 'Template', v: queueStatus.template_sent, c: 'text-zinc-600' },
                ].map((s, i) => (
                  <div key={i}>
                    <div className={`font-data text-base font-bold ${s.c || 'text-zinc-700'}`}>{s.v}</div>
                    <div className="text-zinc-400">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* OVERVIEW TAB — per-step e-mail recipient overzicht */}
        <TabsContent value="overview" className="mt-0">
          <SequenceOverviewTab />
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="mt-0">
          <CampaignTemplatesList
            apiBase={API}
            onSpawn={(newCampaignId) => {
              fetchCampaigns();
              setActiveTab('campaigns');
              // Open product modal for the newly spawned campaign
              fetch(`${API}/email/campaigns/${newCampaignId}`, { credentials: 'include' })
                .then((r) => r.json())
                .then((c) => setProductModalCampaign(c))
                .catch(() => {});
            }}
            refreshKey={productModalCampaign?.campaign_id}
          />
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics" className="mt-0">
          {dashLoading && <div className="flex items-center justify-center h-48"><CircleNotch size={24} className="animate-spin text-[#0055FF]" /></div>}
          {dashData && (() => {
            const o = dashData.overview || {};
            return (
              <div className="space-y-5" data-testid="analytics-dashboard">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-px bg-zinc-200 border border-zinc-200" data-testid="analytics-kpis">
                  {[
                    { l: 'Verzonden', v: o.total_sent, c: 'text-zinc-700' },
                    { l: 'Geopend', v: o.total_opens, c: 'text-cyan-600' },
                    { l: 'Geklikt', v: o.total_clicks, c: 'text-indigo-600' },
                    { l: 'Geantwoord', v: o.total_replies, c: 'text-emerald-600' },
                    { l: 'Open Rate', v: `${o.open_rate}%`, c: 'text-cyan-600' },
                    { l: 'Click Rate', v: `${o.click_rate}%`, c: 'text-indigo-600' },
                    { l: 'Reply Rate', v: `${o.reply_rate}%`, c: 'text-emerald-600' },
                    { l: 'Bounce Rate', v: `${o.bounce_rate}%`, c: o.bounce_rate > 5 ? 'text-red-500' : 'text-zinc-400' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white p-3 text-center">
                      <div className={`font-data text-lg font-bold ${s.c}`}>{s.v}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                    </div>
                  ))}
                </div>

                {/* Funnel */}
                <div className="border border-zinc-200 bg-white p-5" data-testid="analytics-funnel">
                  <h4 className="text-sm font-semibold mb-3">Email Funnel</h4>
                  <div className="flex items-end gap-1 h-32">
                    {[
                      { l: 'Verzonden', v: o.total_sent, c: 'bg-zinc-400' },
                      { l: 'Geopend', v: o.total_opens, c: 'bg-cyan-500' },
                      { l: 'Geklikt', v: o.total_clicks, c: 'bg-indigo-500' },
                      { l: 'Geantwoord', v: o.total_replies, c: 'bg-emerald-500' },
                    ].map((f, i) => {
                      const maxV = Math.max(o.total_sent, 1);
                      const h = Math.max(((f.v / maxV) * 100), 4);
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
                          <span className="font-data text-xs font-bold">{f.v}</span>
                          <div className={`w-full ${f.c} rounded-t-sm transition-all`} style={{ height: `${h}%` }} />
                          <span className="text-[9px] text-zinc-500 uppercase tracking-wider">{f.l}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Timeline Chart */}
                {dashData.timeline?.length > 0 && (
                  <div className="border border-zinc-200 bg-white p-5" data-testid="analytics-timeline">
                    <h4 className="text-sm font-semibold mb-3">Activiteit (laatste 30 dagen)</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={dashData.timeline}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="sent" stackId="1" stroke="#94a3b8" fill="#e2e8f0" name="Verzonden" />
                        <Area type="monotone" dataKey="opened" stackId="2" stroke="#06b6d4" fill="#cffafe" name="Geopend" />
                        <Area type="monotone" dataKey="clicked" stackId="3" stroke="#6366f1" fill="#e0e7ff" name="Geklikt" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Per Step Performance */}
                  {dashData.per_step?.length > 0 && (
                    <div className="border border-zinc-200 bg-white p-5" data-testid="analytics-steps">
                      <h4 className="text-sm font-semibold mb-3">Performance per Stap</h4>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={dashData.per_step}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="step" tick={{ fontSize: 10 }} tickFormatter={v => `Stap ${v}`} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip contentStyle={{ fontSize: 12 }} />
                          <Bar dataKey="sent" fill="#94a3b8" name="Verzonden" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="opened" fill="#06b6d4" name="Geopend" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="clicked" fill="#6366f1" name="Geklikt" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Classification Breakdown */}
                  {dashData.by_classification?.length > 0 && (
                    <div className="border border-zinc-200 bg-white p-5" data-testid="analytics-classification">
                      <h4 className="text-sm font-semibold mb-3">Inbox Classificatie</h4>
                      <div className="space-y-2">
                        {dashData.by_classification.map((c, i) => {
                          const maxC = Math.max(...dashData.by_classification.map(x => x.count), 1);
                          const catStyle = CATEGORY_STYLES[c.category] || CATEGORY_STYLES.other;
                          return (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-xs w-24 truncate">{catStyle.label}</span>
                              <div className="flex-1 h-5 bg-zinc-100 rounded-sm overflow-hidden">
                                <div className="h-full bg-[#0055FF] rounded-sm transition-all" style={{ width: `${(c.count / maxC) * 100}%` }} />
                              </div>
                              <span className="font-data text-xs font-bold w-8 text-right">{c.count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Top Subjects */}
                {dashData.top_subjects?.length > 0 && (
                  <div className="border border-zinc-200 bg-white p-5" data-testid="analytics-top-subjects">
                    <h4 className="text-sm font-semibold mb-3">Top Onderwerpregels (A/B test winnaars)</h4>
                    <div className="space-y-2">
                      {dashData.top_subjects.map((s, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-zinc-50 rounded-sm">
                          <span className="text-sm text-zinc-700 truncate max-w-[70%]">"{s.subject}"</span>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="font-data text-emerald-600 font-bold">{s.open_rate}% open</span>
                            <span className="text-zinc-400">{s.sent} sent</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per Campaign Table */}
                {dashData.campaigns?.length > 0 && (
                  <div className="border border-zinc-200 overflow-hidden" data-testid="analytics-campaigns">
                    <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-200">
                      <h4 className="text-sm font-semibold">Per Campagne</h4>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-200">
                          <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Campagne</th>
                          <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Status</th>
                          <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Sent</th>
                          <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Opens</th>
                          <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Clicks</th>
                          <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Replies</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashData.campaigns.map(c => (
                          <tr key={c.campaign_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                            <td className="px-4 py-2"><div className="font-medium">{c.name}</div></td>
                            <td className="px-3 py-2 text-center"><Badge className={`rounded-full px-2 py-0.5 text-xs font-data ${STATUS_COLORS[c.status] || 'bg-zinc-100'}`}>{c.status}</Badge></td>
                            <td className="px-3 py-2 text-center font-data">{c.stats?.emails_sent || 0}</td>
                            <td className="px-3 py-2 text-center font-data">{c.stats?.unique_opens || 0}</td>
                            <td className="px-3 py-2 text-center font-data">{c.stats?.unique_clicks || 0}</td>
                            <td className="px-3 py-2 text-center font-data">{c.stats?.replies || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* By Country */}
                {dashData.by_country?.length > 0 && (
                  <div className="border border-zinc-200 bg-white p-5" data-testid="analytics-country">
                    <h4 className="text-sm font-semibold mb-3">Per Land</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {dashData.by_country.map((c, i) => (
                        <div key={i} className="text-center p-3 bg-zinc-50 rounded-sm">
                          <div className="font-data text-lg font-bold text-zinc-700">{c.sent}</div>
                          <div className="text-xs text-zinc-500">{c.country}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* AI CONTENT TAB */}
        <TabsContent value="ai-content" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Brain size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">AI Email Generator</h3>
              <Badge className="rounded-full px-2 py-0.5 text-xs font-data bg-blue-100 text-blue-800">GPT-5.2</Badge>
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              Genereer persoonlijke emails met actuele context. Voor elke email wordt live opgehaald:
              het <strong>weer</strong> in de regio, <strong>Google News</strong> over het zwembad, en de <strong>website</strong> van de faciliteit.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Taal</label>
                <Select value={genLanguage} onValueChange={setGenLanguage}>
                  <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Stap</label>
                <Select value={genStep} onValueChange={setGenStep}>
                  <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEQUENCE_STEPS.map(s => <SelectItem key={s.key} value={s.key}>{s.step}. {s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Doelgroep</label>
                <Select value={genSegment} onValueChange={setGenSegment}>
                  <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zwembadbeheerder">Zwembadbeheerder</SelectItem>
                    <SelectItem value="technisch inkoper">Technisch Inkoper</SelectItem>
                    <SelectItem value="zwembadbouwer">Zwembadbouwer</SelectItem>
                    <SelectItem value="distributeur">Distributeur/Dealer</SelectItem>
                    <SelectItem value="facility manager">Facility Manager</SelectItem>
                    <SelectItem value="gemeente">Gemeente/Overheid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border border-dashed border-zinc-300 p-3 rounded-sm mb-4 bg-zinc-50">
              <div className="text-xs font-medium text-zinc-500 mb-2">CRM Context (optioneel)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input data-testid="gen-facility-name" value={genFacilityName} onChange={e => setGenFacilityName(e.target.value)} placeholder="Bedrijfsnaam (bijv. Zwembad De Vennen)" className="rounded-sm text-sm" />
                <Input data-testid="gen-city" value={genCity} onChange={e => setGenCity(e.target.value)} placeholder="Stad (bijv. Dongen)" className="rounded-sm text-sm" />
                <Input data-testid="gen-contact" value={genContactName} onChange={e => setGenContactName(e.target.value)} placeholder="Contactpersoon (bijv. Jan de Vries)" className="rounded-sm text-sm" />
                <Input data-testid="gen-extra" value={genExtraInfo} onChange={e => setGenExtraInfo(e.target.value)} placeholder="Extra info (bijv. Recent gerenoveerd, 25m bad)" className="rounded-sm text-sm" />
              </div>
            </div>
            <div className="flex gap-3">
              <button data-testid="generate-email-btn" onClick={generateContent} disabled={generating}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-50 transition-colors">
                {generating ? <CircleNotch size={14} className="animate-spin" /> : <Lightning size={14} weight="bold" />}
                {generating ? 'Genereren...' : 'Genereer Enkele Email'}
              </button>
              <button data-testid="generate-sequence-btn" onClick={generateFullSequence} disabled={generatingSeq}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {generatingSeq ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                {generatingSeq ? 'Sequence genereren...' : 'Genereer Volledige 6-Staps Sequence'}
              </button>
            </div>
          </div>

          {generatedContent && (
            <div className="border border-emerald-200 bg-emerald-50 p-5 mb-4" data-testid="generated-content">
              <div className="flex items-center gap-2 mb-3">
                <Check size={16} className="text-emerald-600" />
                <span className="font-heading text-sm font-semibold">Gegenereerde Email — {generatedContent.step} ({generatedContent.language})</span>
              </div>
              <div className="space-y-3">
                <div><label className="text-xs font-medium text-zinc-500">Onderwerp</label><div className="text-sm font-medium mt-0.5 p-2 bg-white border border-zinc-200 rounded-sm">{generatedContent.subject}</div></div>
                <div><label className="text-xs font-medium text-zinc-500">Preview tekst</label><div className="text-sm mt-0.5 p-2 bg-white border border-zinc-200 rounded-sm">{generatedContent.preview_text}</div></div>
                <div><label className="text-xs font-medium text-zinc-500">Email inhoud</label><div className="text-sm mt-0.5 p-3 bg-white border border-zinc-200 rounded-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: generatedContent.html_body }} /></div>
              </div>
            </div>
          )}

          {generatedSequence && (
            <div className="space-y-3" data-testid="generated-sequence">
              <div className="flex items-center gap-2 mb-2">
                <Check size={16} className="text-emerald-600" />
                <span className="font-heading text-sm font-semibold">Volledige 6-Staps Sequence ({generatedSequence.length} emails)</span>
              </div>
              {generatedSequence.map((email, i) => (
                <div key={i} className="border border-zinc-200 bg-white">
                  <div className="flex items-center gap-2 px-4 py-2 bg-zinc-50 border-b border-zinc-200">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0055FF] text-white text-xs font-bold">{email.step || i + 1}</span>
                    <span className="text-sm font-medium">{email.name || SEQUENCE_STEPS[i]?.name}</span>
                    <span className="text-xs text-zinc-400 ml-auto">Dag {SEQUENCE_STEPS[i]?.delay || 0}</span>
                  </div>
                  <div className="p-4">
                    <div className="mb-2"><span className="text-xs font-medium text-zinc-500">Onderwerp: </span><span className="text-sm font-medium">{email.subject}</span></div>
                    <div className="text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: email.html_body }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* A/B TESTS TAB */}
        <TabsContent value="ab-tests" className="mt-0">
          {/* Stats Bar */}
          {abStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="ab-stats">
              {[
                { l: 'Totaal', v: abStats.total, c: 'text-zinc-700' },
                { l: 'Draft', v: abStats.draft, c: 'text-zinc-500' },
                { l: 'Running', v: abStats.running, c: 'text-blue-600' },
                { l: 'Afgerond', v: abStats.completed, c: 'text-emerald-600' },
                { l: 'Gem. Uplift', v: `${abStats.avg_uplift}%`, c: 'text-[#0055FF]' },
              ].map((s, i) => (
                <div key={i} className="bg-white p-3 text-center">
                  <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Create Button */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TestTube size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">A/B Tests</h3>
            </div>
            <button data-testid="create-ab-test-btn" onClick={() => setShowCreateAB(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors">
              <Plus size={14} weight="bold" /> Nieuwe A/B Test
            </button>
          </div>

          {/* Create Form */}
          {showCreateAB && (
            <div className="border border-[#0055FF] bg-blue-50 p-5 mb-4" data-testid="create-ab-form">
              <h3 className="font-heading text-base font-semibold mb-3">Nieuwe A/B Test</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Naam</label>
                  <Input data-testid="ab-name" value={abForm.name} onChange={e => setAbForm(f => ({ ...f, name: e.target.value }))} placeholder="Bijv: Onderwerpregel test Q2" className="rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Dimensie</label>
                  <Select value={abForm.dimension} onValueChange={v => setAbForm(f => ({ ...f, dimension: v }))}>
                    <SelectTrigger className="rounded-sm text-sm" data-testid="ab-dimension"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="subject">Onderwerpregel</SelectItem>
                      <SelectItem value="body">Email inhoud</SelectItem>
                      <SelectItem value="send_time">Verzendtijd</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Winnend criterium</label>
                  <Select value={abForm.winning_metric} onValueChange={v => setAbForm(f => ({ ...f, winning_metric: v }))}>
                    <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open_rate">Open Rate</SelectItem>
                      <SelectItem value="click_rate">Click Rate</SelectItem>
                      <SelectItem value="reply_rate">Reply Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Variant inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {['a', 'b'].map(v => (
                  <div key={v} className={`border rounded-sm p-3 ${v === 'a' ? 'border-blue-300 bg-blue-50/50' : 'border-orange-300 bg-orange-50/50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold text-white ${v === 'a' ? 'bg-blue-600' : 'bg-orange-500'}`}>{v.toUpperCase()}</span>
                      <Input value={abForm[`variant_${v}`].label} onChange={e => setAbForm(f => ({ ...f, [`variant_${v}`]: { ...f[`variant_${v}`], label: e.target.value } }))}
                        className="rounded-sm text-sm flex-1 h-8" placeholder="Label" />
                    </div>
                    {abForm.dimension === 'subject' ? (
                      <Input data-testid={`ab-subject-${v}`} value={abForm[`variant_${v}`].subject} onChange={e => setAbForm(f => ({ ...f, [`variant_${v}`]: { ...f[`variant_${v}`], subject: e.target.value } }))}
                        placeholder={`Onderwerpregel ${v.toUpperCase()}`} className="rounded-sm text-sm" />
                    ) : abForm.dimension === 'body' ? (
                      <textarea data-testid={`ab-body-${v}`} value={abForm[`variant_${v}`].text_body} onChange={e => setAbForm(f => ({ ...f, [`variant_${v}`]: { ...f[`variant_${v}`], text_body: e.target.value } }))}
                        placeholder={`Email inhoud ${v.toUpperCase()}`} className="w-full rounded-sm border border-zinc-300 p-2 text-sm min-h-[80px]" />
                    ) : (
                      <Input data-testid={`ab-time-${v}`} type="time" value={abForm[`variant_${v}`].send_time || ''} onChange={e => setAbForm(f => ({ ...f, [`variant_${v}`]: { ...f[`variant_${v}`], send_time: e.target.value } }))}
                        className="rounded-sm text-sm" />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button onClick={aiGenerateVariants} disabled={aiGeneratingVariants} data-testid="ai-generate-variants-btn"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-50 transition-colors">
                  {aiGeneratingVariants ? <CircleNotch size={14} className="animate-spin" /> : <Brain size={14} />}
                  {aiGeneratingVariants ? 'AI genereert...' : 'AI Varianten Genereren'}
                </button>
                <button onClick={createAbTest} disabled={abCreating || !abForm.name} data-testid="save-ab-test-btn"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                  {abCreating ? 'Aanmaken...' : 'Test Aanmaken'}
                </button>
                <button onClick={() => setShowCreateAB(false)} className="px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors">Annuleren</button>
              </div>
            </div>
          )}

          {/* Tests List */}
          <div className="space-y-4" data-testid="ab-tests-list">
            {abTests.length === 0 ? (
              <div className="border border-zinc-200 bg-white p-8 text-center">
                <TestTube size={32} className="mx-auto mb-2 text-zinc-300" />
                <div className="text-sm text-zinc-500">Geen A/B tests. Maak een nieuwe test aan.</div>
              </div>
            ) : abTests.map(t => {
              const isComplete = t.status === 'completed';
              const sa = t.variant_a?.stats || {};
              const sb = t.variant_b?.stats || {};
              const rateA = sa.sent ? ((t.winning_metric === 'click_rate' ? sa.clicked : t.winning_metric === 'reply_rate' ? sa.replied : sa.opened) / sa.sent * 100).toFixed(1) : '0.0';
              const rateB = sb.sent ? ((t.winning_metric === 'click_rate' ? sb.clicked : t.winning_metric === 'reply_rate' ? sb.replied : sb.opened) / sb.sent * 100).toFixed(1) : '0.0';
              const metricLabel = t.winning_metric === 'click_rate' ? 'Click Rate' : t.winning_metric === 'reply_rate' ? 'Reply Rate' : 'Open Rate';

              return (
                <div key={t.test_id} className="border border-zinc-200 bg-white overflow-hidden" data-testid={`ab-test-${t.test_id}`}>
                  {/* Header */}
                  <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TestTube size={18} className={t.status === 'running' ? 'text-blue-600' : isComplete ? 'text-emerald-600' : 'text-zinc-400'} />
                      <div>
                        <div className="font-medium text-sm">{t.name}</div>
                        <div className="text-[11px] text-zinc-400">{t.dimension === 'subject' ? 'Onderwerpregel' : t.dimension === 'body' ? 'Email inhoud' : 'Verzendtijd'} — {metricLabel}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`rounded-sm px-2 py-0.5 text-xs ${
                        t.status === 'running' ? 'bg-blue-100 text-blue-700' :
                        t.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        t.status === 'paused' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'
                      }`}>{t.status}</Badge>
                      {isComplete && t.winner && (
                        <Badge className="rounded-sm px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 flex items-center gap-1">
                          <Trophy size={10} weight="fill" /> Winnaar: {t.winner.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Variant Comparison */}
                  <div className="px-5 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      {['a', 'b'].map(v => {
                        const variant = t[`variant_${v}`] || {};
                        const stats = variant.stats || {};
                        const rate = v === 'a' ? rateA : rateB;
                        const isWinner = isComplete && t.winner === v;
                        return (
                          <div key={v} className={`rounded-sm p-3 border ${isWinner ? 'border-yellow-400 bg-yellow-50' : v === 'a' ? 'border-blue-200 bg-blue-50/30' : 'border-orange-200 bg-orange-50/30'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold text-white ${v === 'a' ? 'bg-blue-600' : 'bg-orange-500'}`}>{v.toUpperCase()}</span>
                                <span className="text-xs font-medium">{variant.label || `Variant ${v.toUpperCase()}`}</span>
                                {isWinner && <Trophy size={14} weight="fill" className="text-yellow-600" />}
                              </div>
                              <span className={`font-data text-lg font-bold ${isWinner ? 'text-yellow-700' : 'text-zinc-700'}`}>{rate}%</span>
                            </div>
                            {t.dimension === 'subject' && variant.subject && (
                              <div className="text-xs text-zinc-600 mb-2 bg-white p-2 rounded-sm border border-zinc-100 truncate">"{variant.subject}"</div>
                            )}
                            <div className="grid grid-cols-4 gap-1 text-center">
                              {[
                                { l: 'Sent', v: stats.sent || 0 },
                                { l: 'Opens', v: stats.opened || 0 },
                                { l: 'Clicks', v: stats.clicked || 0 },
                                { l: 'Replies', v: stats.replied || 0 },
                              ].map((m, i) => (
                                <div key={i}>
                                  <div className="font-data text-sm font-bold text-zinc-700">{m.v}</div>
                                  <div className="text-[9px] text-zinc-400 uppercase">{m.l}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Uplift & Confidence */}
                    {isComplete && (
                      <div className="mt-3 flex items-center justify-center gap-6 py-2 bg-zinc-50 rounded-sm border border-zinc-100">
                        <div className="text-center">
                          <div className="text-xs text-zinc-500">Uplift</div>
                          <div className="font-data text-base font-bold text-emerald-600 flex items-center gap-1">
                            <ArrowUp size={14} weight="bold" /> {t.uplift_pct}%
                          </div>
                        </div>
                        <div className="w-px h-8 bg-zinc-200" />
                        <div className="text-center">
                          <div className="text-xs text-zinc-500">Confidence</div>
                          <div className={`font-data text-base font-bold ${t.confidence_level >= 95 ? 'text-emerald-600' : t.confidence_level >= 80 ? 'text-amber-600' : 'text-zinc-500'}`}>
                            {t.confidence_level}%
                          </div>
                        </div>
                        <div className="w-px h-8 bg-zinc-200" />
                        <div className="text-center">
                          <div className="text-xs text-zinc-500">Winnaar</div>
                          <div className="font-data text-base font-bold text-yellow-700">{t.winner?.toUpperCase()}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50 flex items-center gap-2">
                    {t.status === 'draft' && (
                      <button onClick={() => abTestAction(t.test_id, 'start')} disabled={abActionLoading === `${t.test_id}_start`}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors" data-testid={`start-ab-${t.test_id}`}>
                        {abActionLoading === `${t.test_id}_start` ? <CircleNotch size={12} className="animate-spin" /> : <Play size={12} />} Start
                      </button>
                    )}
                    {t.status === 'running' && (
                      <>
                        <button onClick={() => abTestAction(t.test_id, 'pause')}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-sm hover:bg-amber-600 transition-colors">
                          <Pause size={12} /> Pauzeer
                        </button>
                        <button onClick={() => abTestAction(t.test_id, 'simulate-results')} disabled={abActionLoading === `${t.test_id}_simulate-results`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors" data-testid={`simulate-ab-${t.test_id}`}>
                          {abActionLoading === `${t.test_id}_simulate-results` ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} />} Simuleer Resultaten
                        </button>
                      </>
                    )}
                    <button onClick={() => deleteAbTest(t.test_id)} className="ml-auto p-1.5 hover:bg-red-50 rounded-sm" title="Verwijderen">
                      <Trash size={14} className="text-zinc-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* OPTIMIZATION TAB */}
        <TabsContent value="optimization" className="mt-0">
          {/* Self-Learning Status Banner */}
          <SelfLearningPanel fetchQueueStatus={fetchQueueStatus} />

          {/* Stats Bar */}
          {optInsights && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="opt-stats">
              {[
                { l: 'Optimalisaties', v: optInsights.total_optimizations, c: 'text-zinc-700' },
                { l: 'Toegepast', v: optInsights.applied_count, c: 'text-emerald-600' },
                { l: 'Succes Rate', v: `${optInsights.success_rate}%`, c: 'text-blue-600' },
                { l: 'Gem. Uplift', v: `${optInsights.avg_uplift}%`, c: 'text-[#0055FF]' },
                { l: 'Patronen', v: optInsights.insights?.length || 0, c: 'text-purple-600' },
              ].map((s, i) => (
                <div key={i} className="bg-white p-3 text-center">
                  <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <button data-testid="run-single-opt" onClick={runSingleOptimization} disabled={optRunning}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {optRunning ? <CircleNotch size={14} className="animate-spin" /> : <Lightning size={14} weight="bold" />}
              {optRunning ? 'Optimaliseren...' : 'Run Optimalisatie'}
            </button>
            <button data-testid="run-batch-opt" onClick={runBatchOptimization} disabled={optBatchRunning}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-50 transition-colors">
              {optBatchRunning ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
              {optBatchRunning ? 'Batch draait (3x)...' : 'Batch Optimalisatie (3x)'}
            </button>
            <button data-testid="load-ai-summary" onClick={loadAiSummary} disabled={aiSummaryLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-sm hover:bg-purple-700 disabled:opacity-50 transition-colors">
              {aiSummaryLoading ? <CircleNotch size={14} className="animate-spin" /> : <Brain size={14} />}
              {aiSummaryLoading ? 'AI analyseert...' : 'AI Inzichten Genereren'}
            </button>
          </div>

          {/* AI Summary */}
          {aiSummary && (
            <div className="border-2 border-purple-200 bg-purple-50/40 p-5 mb-5 rounded-sm" data-testid="ai-summary">
              <div className="flex items-center gap-2 mb-3">
                <Brain size={18} weight="bold" className="text-purple-600" />
                <h4 className="font-heading text-base font-semibold text-purple-900">AI Analyse & Aanbevelingen</h4>
                <Badge className="rounded-full px-2 py-0.5 text-xs bg-purple-100 text-purple-700">GPT-5.2</Badge>
              </div>
              <p className="text-sm text-zinc-700 mb-4 leading-relaxed">{aiSummary.summary}</p>

              {aiSummary.top_techniques?.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-purple-700 uppercase tracking-wider mb-2">Top Technieken</div>
                  <div className="flex flex-wrap gap-2">
                    {aiSummary.top_techniques.map((t, i) => (
                      <Badge key={i} className="rounded-sm px-2 py-1 text-xs bg-purple-100 text-purple-800 border border-purple-200">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {aiSummary.recommendations?.length > 0 && (
                <div className="mb-4 space-y-2">
                  <div className="text-xs font-medium text-purple-700 uppercase tracking-wider mb-2">Aanbevelingen</div>
                  {aiSummary.recommendations.map((r, i) => (
                    <div key={i} className="bg-white border border-purple-100 rounded-sm p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-zinc-800">{r.title}</span>
                        {r.expected_impact && <Badge className="rounded-sm px-2 py-0.5 text-[10px] bg-emerald-100 text-emerald-700">{r.expected_impact}</Badge>}
                      </div>
                      <p className="text-xs text-zinc-600">{r.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {aiSummary.avoid?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-red-600 uppercase tracking-wider mb-2">Vermijden</div>
                  <div className="flex flex-wrap gap-2">
                    {aiSummary.avoid.map((a, i) => (
                      <Badge key={i} className="rounded-sm px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200">{a}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Learned Patterns */}
          {optInsights?.insights?.length > 0 && (
            <div className="border border-zinc-200 bg-white p-5 mb-5 rounded-sm" data-testid="learned-patterns">
              <div className="flex items-center gap-2 mb-4">
                <ChartBar size={18} weight="bold" className="text-[#0055FF]" />
                <h4 className="font-heading text-base font-semibold">Geleerde Patronen</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {optInsights.insights.map((ins, i) => {
                  const winRate = ins.total_tests > 0 ? Math.round((ins.wins || 0) / ins.total_tests * 100) : 0;
                  return (
                    <div key={i} className="border border-zinc-200 rounded-sm p-3" data-testid={`pattern-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-zinc-800 truncate">{ins.pattern}</span>
                        <span className="text-xs font-data font-bold text-emerald-600">{ins.avg_uplift || 0}%</span>
                      </div>
                      <p className="text-xs text-zinc-500 mb-2 line-clamp-2">{ins.description || 'Geen beschrijving'}</p>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-zinc-400">Tests: <span className="font-data text-zinc-600">{ins.total_tests || 0}</span></span>
                        <span className="text-zinc-400">Wins: <span className="font-data text-emerald-600">{ins.wins || 0}</span></span>
                        <span className="text-zinc-400">Win%: <span className={`font-data ${winRate >= 70 ? 'text-emerald-600' : winRate >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{winRate}%</span></span>
                      </div>
                      {/* Win rate bar */}
                      <div className="mt-2 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${winRate}%` }} />
                      </div>
                      {ins.example_subject && (
                        <div className="mt-2 text-[11px] text-zinc-400 italic truncate">"{ins.example_subject}"</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Optimization History */}
          <div className="border border-zinc-200 bg-white overflow-hidden rounded-sm" data-testid="opt-history">
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
              <ArrowsClockwise size={16} className="text-zinc-500" />
              <span className="text-sm font-semibold">Optimalisatie Historiek</span>
              <span className="text-xs text-zinc-400 ml-auto">{optHistory.length} items</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Origineel</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Geoptimaliseerd</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Techniek</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Uplift</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Winnaar</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Toegepast</th>
                </tr>
              </thead>
              <tbody>
                {optHistory.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400 text-xs">
                    Geen optimalisaties. Klik "Run Optimalisatie" om te starten.
                  </td></tr>
                ) : optHistory.map((h, i) => (
                  <tr key={h.optimization_id || i} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2.5 text-xs max-w-[180px] truncate text-zinc-600" title={h.original}>{h.original || '(leeg)'}</td>
                    <td className="px-4 py-2.5 text-xs max-w-[180px] truncate font-medium text-zinc-800" title={h.optimized}>{h.optimized}</td>
                    <td className="px-4 py-2.5">
                      <Badge className="rounded-sm px-2 py-0.5 text-[10px] bg-purple-50 text-purple-700 border border-purple-200">{h.technique}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="font-data text-sm font-bold text-emerald-600 flex items-center justify-center gap-0.5">
                        <ArrowUp size={10} weight="bold" /> {h.uplift_pct}%
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold text-white ${h.winner === 'b' ? 'bg-orange-500' : 'bg-blue-600'}`}>
                        {h.winner?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {h.applied ? <Check size={16} className="mx-auto text-emerald-500" /> : <X size={16} className="mx-auto text-zinc-300" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* SEGMENTS TAB */}
        <TabsContent value="segments" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-4">
              <Funnel size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">Segment Builder</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-4">Bouw doelgroepen op basis van land, taal, type, rol en lead score.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Land</label>
                <Select value={segmentFilter.country_code || ''} onValueChange={v => setSegmentFilter(f => ({ ...f, country_code: v || undefined }))}>
                  <SelectTrigger className="rounded-sm text-sm"><SelectValue placeholder="Alle landen" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="NL">Nederland</SelectItem><SelectItem value="BE">Belgie</SelectItem>
                    <SelectItem value="DE">Duitsland</SelectItem><SelectItem value="FR">Frankrijk</SelectItem>
                    <SelectItem value="GB">Verenigd Koninkrijk</SelectItem><SelectItem value="ES">Spanje</SelectItem>
                    <SelectItem value="IT">Italie</SelectItem><SelectItem value="PL">Polen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Status</label>
                <Select value={segmentFilter.customer_status || ''} onValueChange={v => setSegmentFilter(f => ({ ...f, customer_status: v || undefined }))}>
                  <SelectTrigger className="rounded-sm text-sm"><SelectValue placeholder="Alle statussen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect (nieuwe lead)</SelectItem>
                    <SelectItem value="active_customer">Bestaande klant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Min. Lead Score</label>
                <Input type="number" value={segmentFilter.min_lead_score || ''} onChange={e => setSegmentFilter(f => ({ ...f, min_lead_score: e.target.value ? parseInt(e.target.value) : undefined }))} placeholder="0" className="rounded-sm text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={previewSegment} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors"><Users size={14} /> Preview Segment</button>
              {segmentCount !== null && <span className="text-sm font-data font-bold text-[#0055FF]" data-testid="segment-count">{segmentCount} contacten gevonden</span>}
            </div>
          </div>
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings" className="mt-0">
          <DailyDigestSettings />
          <SocialProofSettings />
          <SendWindowSettings />
          <QueueSettings />
          <InboxAutoSyncSettings />
          <InboxCleanupSettings />
        </TabsContent>
      </Tabs>

      {/* Activate Confirmation Dialog */}
      {activateDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setActivateDialog(null)}>
          <div className="bg-white rounded-sm shadow-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()} data-testid="activate-dialog">
            <h3 className="font-heading text-lg font-semibold mb-2">Campagne Activeren</h3>
            {activateDialog.loading ? (
              <div className="flex items-center gap-2 py-4 text-sm text-zinc-500"><CircleNotch size={16} className="animate-spin" /> Contacten tellen...</div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-zinc-600">
                  Deze campagne wordt geactiveerd voor <strong className="text-[#0055FF] font-data text-lg">{activateDialog.count}</strong> contacten.
                </p>
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs text-zinc-500 space-y-1">
                  <div>Per contact worden <strong>6 emails</strong> ingepland:</div>
                  <div>Stap 1: direct | Stap 2: +3 dagen | Stap 3: +7 dagen</div>
                  <div>Stap 4: +12 dagen | Stap 5: +18 dagen | Stap 6: +25 dagen</div>
                  <div className="mt-2 text-emerald-600">Afgemelde contacten worden automatisch overgeslagen.</div>
                  <div className="text-amber-600">Bij reply stopt de sequence en wordt een conversatie aangemaakt.</div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={doActivate} disabled={activating} data-testid="confirm-activate-btn"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                    {activating ? <CircleNotch size={14} className="animate-spin" /> : <Play size={14} />}
                    {activating ? 'Activeren...' : `Activeer voor ${activateDialog.count} contacten`}
                  </button>
                  <button onClick={() => setActivateDialog(null)} className="px-4 py-2.5 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors">
                    Annuleren
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Graph Status Banner */}
      <GraphStatusBanner />

      {/* Broadcast Editor */}
      {broadcastCampaign && (
        <BroadcastEditor
          campaign={broadcastCampaign}
          onClose={() => setBroadcastCampaign(null)}
          onSent={fetchCampaigns}
        />
      )}

      {/* Sender / signature editor voor bestaande campagnes */}
      {editCampaign && (
        <CampaignEditModal
          campaign={editCampaign}
          onClose={() => setEditCampaign(null)}
          onSaved={() => { setEditCampaign(null); fetchCampaigns(); }}
        />
      )}

      {/* Dupliceer campagne (incl. cross-profile) */}
      {duplicateCampaign && (
        <CampaignDuplicateModal
          campaign={duplicateCampaign}
          onClose={() => setDuplicateCampaign(null)}
          onCreated={() => { setDuplicateCampaign(null); fetchCampaigns(); }}
        />
      )}

      {/* Queue inspector — bekijk wie wanneer welke stap krijgt + handmatig contacten uit loop halen */}
      {queueCampaign && (
        <CampaignQueueInspectorModal
          campaign={queueCampaign}
          onClose={() => setQueueCampaign(null)}
        />
      )}

      {/* Voeg nieuwe contacten toe aan lopende campagne */}
      {addContactsCampaign && (
        <AddNewContactsModal
          campaign={addContactsCampaign}
          onClose={() => setAddContactsCampaign(null)}
          onDone={() => fetchCampaigns()}
        />
      )}

      {/* Broadcast Stats Drill-down */}
      {broadcastStatsId && (
        <BroadcastDetailPanel
          campaignId={broadcastStatsId}
          onClose={() => setBroadcastStatsId(null)}
          onCreateFollowUp={(newCampaign) => {
            fetchCampaigns();
            setBroadcastStatsId(null);
            setBroadcastCampaign(newCampaign);
          }}
        />
      )}

      {/* Per-campaign Product & Sequence modal */}
      <CampaignProductModal
        open={!!productModalCampaign}
        campaign={productModalCampaign}
        apiBase={API}
        onClose={() => setProductModalCampaign(null)}
        onSaved={() => fetchCampaigns()}
      />

      {/* Cross-profile Sequence Library modal */}
      <SequenceLibraryModal
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        apiBase={API}
        activeProfile={overview?.active_profile || ''}
        campaigns={campaigns}
        onImported={() => fetchCampaigns()}
      />
    </div>
  );
}
